import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
} from '@mui/material';
import {
  Agriculture as AgricultureIcon,
  Inventory as InventoryIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

interface ProductTypeSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelectType: (productType: 'A' | 'B') => void;
}

const ProductTypeSelectionModal: React.FC<ProductTypeSelectionModalProps> = ({
  open,
  onClose,
  onSelectType,
}) => {
  const handleSelectProductA = () => {
    onSelectType('A');
  };

  const handleSelectProductB = () => {
    onSelectType('B');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disablePortal
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" component="div" fontWeight={600}>
          Seleccionar Tipo de Producto
        </Typography>
        <Button
          onClick={onClose}
          size="small"
          color="inherit"
          sx={{ minWidth: 'auto', p: 0.5 }}
        >
          <CloseIcon fontSize="small" />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Seleccione el tipo de producto para crear el DERC correspondiente:
        </Typography>

        <Grid container spacing={3}>
          {/* Producto A */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                height: '100%',
                border: '2px solid',
                borderColor: 'primary.main',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-2px)',
                },
              }}
              onClick={handleSelectProductA}
            >
              <CardContent sx={{ textAlign: 'center', pb: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <AgricultureIcon
                      sx={{
                        fontSize: 40,
                        color: 'white',
                      }}
                    />
                  </Box>
                </Box>

                <Typography variant="h5" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                  Producto A
                </Typography>

                <Chip
                  label="DERC Estándar"
                  color="primary"
                  size="small"
                  sx={{ mb: 2 }}
                />

                <Typography variant="body2" color="text.secondary">
                  Materia prima estándar para procesamiento regular.
                  Se creará un DERC tipo A para el seguimiento del producto.
                </Typography>
              </CardContent>

              <CardActions sx={{ justifyContent: 'center', pt: 0 }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  sx={{
                    mx: 2,
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Seleccionar Producto A
                </Button>
              </CardActions>
            </Card>
          </Grid>

          {/* Producto B */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                height: '100%',
                border: '2px solid',
                borderColor: 'error.main',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-2px)',
                },
              }}
              onClick={handleSelectProductB}
            >
              <CardContent sx={{ textAlign: 'center', pb: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: 'error.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <InventoryIcon
                      sx={{
                        fontSize: 40,
                        color: 'white',
                      }}
                    />
                  </Box>
                </Box>

                <Typography variant="h5" fontWeight={600} color="error" sx={{ mb: 1 }}>
                  Producto B
                </Typography>

                <Chip
                  label="DERC Especial"
                  color="error"
                  size="small"
                  sx={{ mb: 2 }}
                />

                <Typography variant="body2" color="text.secondary">
                  Materia prima especial que requiere tratamiento diferenciado.
                  Se creará un DERC tipo B para el control específico.
                </Typography>
              </CardContent>

              <CardActions sx={{ justifyContent: 'center', pt: 0 }}>
                <Button
                  variant="contained"
                  color="error"
                  fullWidth
                  size="large"
                  sx={{
                    mx: 2,
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Seleccionar Producto B
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          color="inherit"
          variant="outlined"
          sx={{ fontWeight: 500 }}
        >
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductTypeSelectionModal;